import { defineStore } from 'pinia';
import { fetchWrapper } from '@/utils/helpers/fetch-wrapper';

/**
 * Runtime feature flags fetched from GET /feature-flags (bsapi). Lets us
 * hide/show menu items and block their routes PER ENVIRONMENT without a code
 * change or rebuild — the backend reads HIDDEN_FEATURES from its own .env, so
 * staging and production (separate servers) can differ. Flip the env var and it
 * takes effect on the next page load.
 *
 * Fails OPEN: if the fetch errors or hasn't run yet, nothing is hidden — better
 * to show a menu we meant to hide than to blank the whole nav on a hiccup.
 *
 * Known keys: 'mat_ai', 'quotes'.
 */
interface FeatureFlagsState {
    hidden: string[];
    // Per-user hides derived from the account's admin feature toggles
    // (Mat AI / Smart Quote). Kept separate from the env-driven `hidden` list
    // so a re-fetch of either source doesn't clobber the other; `isHidden`
    // treats a key hidden if it appears in EITHER list. Populated by
    // planAccess.load() via applyUserFlags().
    userHidden: string[];
    loaded: boolean;
    loading: Promise<void> | null;
}

export const useFeatureFlagsStore = defineStore('featureFlags', {
    state: (): FeatureFlagsState => ({
        hidden: [],
        userHidden: [],
        loaded: false,
        loading: null,
    }),

    getters: {
        isLoaded: (state) => state.loaded,
        // Method-style getter so template `computed`s track state. A feature is
        // hidden if the environment hides it OR the account's admin toggle for
        // it is off (Mat AI / Smart Quote default off for everyone).
        isHidden: (state) => (key: string): boolean =>
            state.hidden.includes(key) || state.userHidden.includes(key),
    },

    actions: {
        async load(force = false): Promise<void> {
            if (this.loaded && !force) return;
            if (this.loading) return this.loading;

            const baseUrl = (import.meta as any).env?.VITE_API_URL_API || '/api';

            this.loading = (async () => {
                try {
                    const body: any = await fetchWrapper.get(`${baseUrl}/feature-flags`);
                    this.hidden = Array.isArray(body?.hidden) ? body.hidden : [];
                    this.loaded = true;
                } catch {
                    // Fail open — leave nothing hidden.
                    this.hidden = [];
                    this.loaded = false;
                } finally {
                    this.loading = null;
                }
            })();
            return this.loading;
        },

        /**
         * Merge the account's admin feature toggles into the per-user hidden
         * set. Mat AI and Smart Quote are OFF for everyone unless Built Simple
         * staff switch them on for the account, so a missing/false flag hides
         * the feature. Called by planAccess.load() once the subscription
         * payload (which carries feature_mat_ai / feature_smart_quote) lands.
         */
        applyUserFlags(flags: { feature_mat_ai?: boolean | null; feature_smart_quote?: boolean | null } | null | undefined): void {
            const hides: string[] = [];
            if (flags?.feature_mat_ai !== true) hides.push('mat_ai');
            if (flags?.feature_smart_quote !== true) hides.push('quotes');
            this.userHidden = hides;
        },

        reset() {
            this.hidden = [];
            this.userHidden = [];
            this.loaded = false;
            this.loading = null;
        },
    },
});
